import React, { FC, useCallback, useMemo, useState } from "react";

import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { omit } from "lodash-es";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";
import { SearchField } from "@mapmycustomers/ui";

import localSettings from "@app/config/LocalSettings";
import { NoteFieldName } from "@app/util/fieldModel/NoteFieldModel";

import styles from "./Notes.module.scss";

interface SearchFieldProps {
  filter?: FilterModel;
  onFetchNotes?: (payload?: { filter?: FilterModel; sort?: SortModel }) => void;
}

const NotesSearch: FC<SearchFieldProps> = ({ filter, onFetchNotes }) => {
  const intl = useIntl();

  const [open, setOpen] = useState(false);
  const recentSearches = localSettings.getNotesRecentSearches();

  const handleSearch = useCallback(
    (value: string) => {
      if (!isEmptyString(value)) {
        localSettings.updateNotesRecentSearches(value);
      }
      onFetchNotes?.({
        filter: isEmptyString(value)
          ? omit(filter, NoteFieldName.NOTE)
          : {
              ...filter,
              [NoteFieldName.NOTE]: {
                operator: FilterOperator.CONTAINS,
                value: value,
              },
            },
      });
    },
    [filter, onFetchNotes]
  );
  const [search, setSearch] = useStateWithDebouncedListener([handleSearch, 500], "");

  const menu: MenuProps = useMemo(
    () => ({
      items: recentSearches.map((search, index) => ({
        key: index,
        label: (
          <div className={styles.searchItem}>
            <FontAwesomeIcon className={styles.clock} icon={faClock} />
            <span>{search}</span>
          </div>
        ),
        onClick: () => {
          setSearch(search);
        },
      })),
    }),
    [recentSearches, setSearch]
  );

  return (
    <Dropdown
      autoFocus
      menu={menu}
      onOpenChange={setOpen}
      open={open && !search && recentSearches.length > 0}
      trigger={["click"]}
    >
      <SearchField
        allowClear
        onChange={setSearch}
        placeholder={intl.formatMessage({
          id: "recordPane.noteList.search.placeholder",
          defaultMessage: "Search",
          description: "Placeholder of notes search field",
        })}
        value={search}
      />
    </Dropdown>
  );
};

export default NotesSearch;
